/**
 * Asset deployment — copies assets into framework dirs by default (self-contained,
 * cache-safe, portable); `--link` opts into a symlink for the local-path dev loop.
 */

import {
  cpSync,
  existsSync,
  lstatSync,
  mkdirSync,
  rmSync,
  statSync,
  symlinkSync,
  unlinkSync,
  writeFileSync,
} from "node:fs";
import { basename, dirname, join, resolve } from "node:path";
import type {
  AiComponent,
  CatalogEntry,
  DriverTarget,
  SourceDeclaration,
} from "@skaile/workspaces/core";
import {
  applyPatch,
  computeHash,
  DRIVER_TARGETS,
  deployedBase,
  deployedDir,
  isDeployed,
  resolveSkWorkspaceConfig,
} from "@skaile/workspaces/core";
import { installAgent } from "./install-agent.js";
import type { Log } from "./renderers.js";

export type { Log } from "./renderers.js";

const noop: Log = () => {};

// ── Filesystem helpers ────────────────────────────────────────────────────────

const DIRECTORY_MANIFESTS = new Set(["SKILL.md", "AGENT.md", "agent.yaml", "CONTRACT.md"]);

function clearDest(dest: string): void {
  try {
    const s = lstatSync(dest);
    if (s.isSymbolicLink()) unlinkSync(dest);
    else rmSync(dest, { recursive: true, force: true });
  } catch {}
}

function copyFromManifest(manifestPath: string, dest: string): void {
  const name = basename(manifestPath);
  const parentDir = dirname(manifestPath);
  clearDest(dest);

  // Source resolved to a directory itself (not a manifest file) — copy it whole;
  // a plain cpSync without `recursive` throws on a directory.
  if (statSync(manifestPath).isDirectory()) {
    cpSync(manifestPath, dest, { recursive: true });
  } else if (DIRECTORY_MANIFESTS.has(name) || basename(parentDir) === basename(dest)) {
    cpSync(parentDir, dest, { recursive: true });
  } else {
    mkdirSync(dest, { recursive: true });
    cpSync(manifestPath, join(dest, name));
  }
}

function symlinkFromManifest(manifestPath: string, dest: string): void {
  const name = basename(manifestPath);
  const parentDir = dirname(manifestPath);
  clearDest(dest);
  mkdirSync(dirname(dest), { recursive: true });

  if (statSync(manifestPath).isDirectory()) {
    symlinkSync(resolve(manifestPath), dest);
  } else if (DIRECTORY_MANIFESTS.has(name) || basename(parentDir) === basename(dest)) {
    symlinkSync(resolve(parentDir), dest);
  } else {
    mkdirSync(dest, { recursive: true });
    symlinkSync(resolve(manifestPath), join(dest, name));
  }
}

// ── Deploy options ───────────────────────────────────────────────────────────

/**
 * Options controlling where and how assets are deployed.
 *
 * @docLink packages/asset-manager/api-reference#installer
 */
export interface DeployOptions {
  /** Agent framework to target (determines framework-specific directory layout). Defaults to `"claude-code"`. */
  driverTarget?: DriverTarget;
  /** When `true`, deploy into the global Skaile cache rather than a project workspace. */
  global?: boolean;
  /** Absolute path to the workspace root. Defaults to `process.cwd()`. */
  cwd?: string;
  /**
   * Opt into a symlink (the author dev-loop) instead of a copy — honoured only
   * when the source has a real local path. Machine-local / not for committing;
   * for store/factory sources (no stable local path) it is a no-op (still copy).
   */
  link?: boolean;
}

// ── Deploy / remove ──────────────────────────────────────────────────────────

/**
 * Deploy a single asset to the framework-specific directory.
 *
 * Copies by default — self-contained, cache-safe, and portable, so a deployed
 * asset survives a `cache clean` of its source and a committed `.claude/` stays
 * machine-independent. `opts.link` opts into a symlink (the live dev-loop), but
 * only when the source has a real local path; store/factory sources (no stable
 * local path) ignore it and still copy. Agents are rendered via the enriched
 * `installAgent` path which resolves abilities, contracts, and framework
 * fragments before writing the native agent file.
 *
 * @param entry - Catalog entry describing the asset to deploy.
 * @param repositories - Repository declarations from `skaile.yaml` (used to
 *   determine whether the source is a local path or a remote clone).
 * @param opts - Deployment target options.
 * @param log - Optional logger callback for progress messages.
 * @returns `true` when the asset was deployed successfully.
 * @docLink packages/asset-manager/api-reference#installer
 */
export function deployAsset(
  entry: CatalogEntry,
  repositories: Record<string, SourceDeclaration>,
  opts: DeployOptions = {},
  log: Log = noop,
): boolean {
  const driverTarget = opts.driverTarget ?? "claude-code";
  const globalInstall = opts.global ?? false;
  const cwd = opts.cwd;

  // Bundles are not deployed directly — their dependencies are.
  // MCP servers are configured in skaile.yaml, not deployed to driver target dirs.
  if (entry.kind === "bundle" || entry.kind === "mcp-server") return true;

  // Agents need framework-specific rendering via the enriched installAgent path
  if (entry.kind === "agent") {
    const workspaceRoot = cwd ?? process.cwd();
    const agentDeployDir = deployedBase(
      "agent" as AiComponent,
      driverTarget as DriverTarget,
      globalInstall,
      cwd,
    );
    const targetMap = DRIVER_TARGETS[driverTarget as DriverTarget];
    const skillsRelDir = targetMap?.local.skill ?? "";
    const wsConfig = resolveSkWorkspaceConfig(workspaceRoot);
    const name = basename(entry.source);
    const srcDir = DIRECTORY_MANIFESTS.has(name) ? dirname(entry.source) : entry.source;
    const result = installAgent(
      srcDir,
      agentDeployDir,
      workspaceRoot,
      driverTarget,
      skillsRelDir,
      entry.name,
      /* includeSubAgents */ true,
      new Set<string>(),
      wsConfig,
      log,
    );
    for (const w of result.warnings) log(`  warn: ${w}`);
    if (result.created.length > 0) return true;
    // Fall through to raw deploy if renderer produces no files (unsupported framework, etc.)
  }

  const dest = deployedDir(entry.kind as AiComponent, entry.name, driverTarget, globalInstall, cwd);
  mkdirSync(dirname(dest), { recursive: true });

  // Symlink only on explicit opt-in AND when the source has a real local path.
  // Factory sources have a `path` (the bundled dir under node_modules, replaced
  // on every upgrade) but no stable dev-loop, so they always copy. Default copy
  // keeps deploys cache-safe and portable.
  const repoDecl = entry.publisher ? repositories[entry.publisher] : undefined;
  const hasLocalPath = repoDecl?.path != null && repoDecl.factory !== true;

  if (opts.link === true && hasLocalPath) {
    symlinkFromManifest(entry.source, dest);
    log(`  symlink ${entry.kind}:${entry.name}`);
    return true;
  } else {
    copyFromManifest(entry.source, dest);
    log(`  copy ${entry.kind}:${entry.name}`);
    return true;
  }
}

/**
 * Deploy all resolved assets, applying patches where configured.
 *
 * Already-deployed assets are skipped unless their deployed manifest content differs
 * from the source. This handles copies that have gone stale after a source update.
 * In copy mode (the default, `!opts.link`) an existing **symlink** dest is healed:
 * it is removed and re-deployed as a real copy, so an old/legacy symlink deploy is
 * silently upgraded. With `opts.link` an existing symlink is left alone.
 *
 * @param resolved - Catalog entries to deploy (output of `resolveAll`).
 * @param repositories - Repository declarations from `skaile.yaml`.
 * @param patches - Map of `kind:name` → patch file path for patch application.
 * @param opts - Deployment target options.
 * @param log - Optional logger callback for progress messages.
 * @returns Array of asset refs (`kind:name`) that were newly deployed in this call.
 * @docLink packages/asset-manager/api-reference#installer
 */
export function deployAll(
  resolved: CatalogEntry[],
  repositories: Record<string, SourceDeclaration>,
  patches: Record<string, string>,
  opts: DeployOptions = {},
  log: Log = noop,
): string[] {
  const deployed: string[] = [];
  const driverTarget = opts.driverTarget ?? "claude-code";
  const globalInstall = opts.global ?? false;
  const cwd = opts.cwd;

  for (const entry of resolved) {
    // Skip non-deployable asset kinds — bundles are resolved to their contents,
    // mcp-servers are wired at session startup, and mixin kinds (persona, ruleset,
    // knowledge) are resolved by the mixin-resolver at session creation time.
    if (
      entry.kind === "bundle" ||
      entry.kind === "mcp-server" ||
      entry.kind === "persona" ||
      entry.kind === "ruleset" ||
      entry.kind === "knowledge"
    )
      continue;

    const ref = `${entry.kind}:${entry.name}`;

    if (isDeployed(entry.kind as AiComponent, entry.name, driverTarget, globalInstall, cwd)) {
      // Compare the deployed manifest file against the source to detect stale copies.
      // For symlinks the deployed path resolves through to the source file, so hashes
      // always match — symlinked installs are never needlessly re-linked.
      const dest = deployedDir(
        entry.kind as AiComponent,
        entry.name,
        driverTarget,
        globalInstall,
        cwd,
      );
      // Heal: in copy mode an existing symlink dest is left to fall through to a
      // real copy below (cache-safe + portable) — bypass the hash skip entirely.
      // With --link a symlink dest is intentional, so it takes the normal skip path.
      let destIsSymlink = false;
      try {
        destIsSymlink = lstatSync(dest).isSymbolicLink();
      } catch {}
      const healSymlink = destIsSymlink && opts.link !== true;
      // Surface the one-time symlink→copy migration so an author who relied on a
      // live symlink dev-loop knows why edits stopped reflecting.
      if (healSymlink) log(`  healed symlink → copy: ${ref} (use --link for the dev-loop)`);

      if (!healSymlink) {
        const manifestName = basename(entry.source);
        const deployedManifest = join(dest, manifestName);
        // isDeployed() only lstats the dir, so a present-but-empty dir (manifest
        // file deleted) reads as deployed. A missing manifest must never count as
        // "already deployed" — fall through to re-deploy instead of skipping.
        // (existsSync follows symlinks, so for symlinked installs the manifest
        // resolves through to the live source and the hash check below still
        // skips them.)
        if (existsSync(deployedManifest)) {
          try {
            const srcHash = computeHash(entry.source);
            const depHash = computeHash(deployedManifest);
            if (srcHash === depHash) {
              log(`  already deployed: ${ref}`);
              continue;
            }
            log(`  updating: ${ref} (content changed)`);
            // fall through to redeploy
          } catch {
            // Deployed manifest unreadable (e.g. agent rendered to a different path) — skip
            log(`  already deployed: ${ref}`);
            continue;
          }
        }
      }
    }

    deployAsset(entry, repositories, opts, log);
    deployed.push(ref);

    // Apply patch if configured
    const patchFile = patches[ref];
    if (patchFile && existsSync(patchFile)) {
      const dest = deployedDir(
        entry.kind as AiComponent,
        entry.name,
        driverTarget,
        globalInstall,
        cwd,
      );
      if (applyPatch(dest, patchFile)) {
        log(`  patched ${ref}`);
      } else {
        log(`  patch failed for ${ref}`);
      }
    }
  }

  return deployed;
}

/**
 * Remove a deployed asset from the framework directory.
 *
 * Removes symlinks with `unlink`; removes copied directories with `rm -rf`.
 *
 * @param kind - Asset kind (`skill`, `agent`, `prompt`, `flow`, `contract`).
 * @param name - Asset name as registered in the catalog.
 * @param opts - Deployment target options (must match the options used at install time).
 * @returns `true` if the asset existed and was removed; `false` if it was not deployed.
 * @docLink packages/asset-manager/api-reference#installer
 */
export function removeAsset(kind: string, name: string, opts: DeployOptions = {}): boolean {
  const driverTarget = opts.driverTarget ?? "claude-code";
  const globalInstall = opts.global ?? false;
  const cwd = opts.cwd;

  const dest = deployedDir(kind as AiComponent, name, driverTarget, globalInstall, cwd);
  try {
    const s = lstatSync(dest);
    if (s.isSymbolicLink()) unlinkSync(dest);
    else rmSync(dest, { recursive: true, force: true });
    return true;
  } catch {
    return false;
  }
}

// ── Scaffold ─────────────────────────────────────────────────────────────────

const SKILL_TEMPLATE = `---
name: "{name}"
description: ""
metadata:
  version: "0.1.0"
---

# {name}

Skill prompt goes here.
`;

const BUNDLE_TEMPLATE = `name: {name}
description: A curated set of assets
version: 0.1.0
dependencies:
  - skill:example-skill
`;

const FLOW_TEMPLATE = `id: "{name}"
version: "0.1.0"
name: "{name}"
description: Flow description

globals:
  approval_mode: checkpoint

nodes: []
`;

/**
 * Create a minimal scaffold directory for a new asset.
 *
 * Writes the appropriate manifest file (`SKILL.md`, `AGENT.md`, `CONTRACT.md`,
 * `<name>.bundle.yaml`, `<name>.flow.yaml`, or `<name>.prompt.md`) inside a
 * new `<destDir>/<name>/` directory.
 *
 * @param name - Asset name (also used as the directory name).
 * @param kind - Asset kind: `skill` | `agent` | `bundle` | `flow` | `prompt` | `contract`.
 * @param destDir - Parent directory to create the asset in.
 * @returns `{ ok: true, path }` on success, `{ ok: false, path: errorMessage }` if the
 *   directory already exists.
 * @docLink packages/asset-manager/api-reference#installer
 */
export function createScaffold(
  name: string,
  kind: string,
  destDir: string,
): { ok: boolean; path: string } {
  const dest = join(destDir, name);
  if (existsSync(dest)) {
    return { ok: false, path: `Directory '${name}' already exists in ${destDir}` };
  }
  mkdirSync(dest, { recursive: true });

  if (kind === "bundle") {
    writeFileSync(
      join(dest, `${name}.bundle.yaml`),
      BUNDLE_TEMPLATE.replace(/\{name\}/g, name),
      "utf8",
    );
  } else if (kind === "flow") {
    writeFileSync(
      join(dest, `${name}.flow.yaml`),
      FLOW_TEMPLATE.replace(/\{name\}/g, name),
      "utf8",
    );
  } else if (kind === "prompt") {
    writeFileSync(
      join(dest, `${name}.prompt.md`),
      SKILL_TEMPLATE.replace(/\{name\}/g, name),
      "utf8",
    );
  } else if (kind === "agent") {
    writeFileSync(join(dest, "AGENT.md"), SKILL_TEMPLATE.replace(/\{name\}/g, name), "utf8");
  } else if (kind === "contract") {
    writeFileSync(join(dest, "CONTRACT.md"), SKILL_TEMPLATE.replace(/\{name\}/g, name), "utf8");
  } else {
    // Default: skill
    writeFileSync(join(dest, "SKILL.md"), SKILL_TEMPLATE.replace(/\{name\}/g, name), "utf8");
  }

  return { ok: true, path: dest };
}
